import {
  useDrilldownSchema,
  useVersionAwareDrilldownPanelData,
} from 'features/drilldown'
import { useFilteredEntryCount } from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import { DrilldownRolodexType } from 'features/drilldown/rolodex/ontology/ontologyTypes'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { GenericPanelData, RolodexHistogramFilter } from 'types'

import { ScheduledInspectionCountView } from './CustomRenderers'
import { StatDisplayCount } from './statDisplay/StatDisplayCount'

const Container = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-evenly;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`

export type DrilldownCountConfig = {
  countType: string
  rolodexType: DrilldownRolodexType
  title: string
  filters?: ReadonlyArray<RolodexHistogramFilter>
}

export const COMPLETED_INSPECTION_FILTER: ReadonlyArray<RolodexHistogramFilter> =
  [
    {
      type: 'component-dataset',
      field: 'id',
      op: 'notnull',
    },
  ]

export const SPECIAL_COUNT_PANEL_CONFIGS: ReadonlyArray<DrilldownCountConfig> =
  [
    {
      countType: 'completedInspections',
      rolodexType: 'inspection',
      title: 'Inspections Completed',
      filters: COMPLETED_INSPECTION_FILTER,
    },
    {
      countType: 'scheduledInspections',
      rolodexType: 'inspection',
      title: 'Inspections Scheduled',
    },
  ]

export function useCountConfigs(): ReadonlyArray<DrilldownCountConfig> {
  const { entryTypeNames } = useDrilldownSchema()
  return useMemo(
    () => [
      ...Object.entries(entryTypeNames).map(([key, value]) => ({
        countType: `count_${key}`,
        rolodexType: key,
        title: value,
      })),
      ...Object.values(SPECIAL_COUNT_PANEL_CONFIGS),
    ],
    [entryTypeNames]
  )
}

export const COUNT_PANEL_CUSTOM_RENDERERS: Partial<
  Record<string, React.ReactNode>
> = {
  scheduledInspections: (
    <ScheduledInspectionCountView title='Scheduled Inspections' />
  ),
}

export interface CountsPanelData extends GenericPanelData {
  activeCountTypes: ReadonlyArray<string>
}

export const LATEST_COUNTS_PANEL_DATA_SCHEMA_VERSION = 3

export const DEFAULT_COUNTS_PANEL_DATA: CountsPanelData = {
  activeCountTypes: [
    'count_unit',
    'completedInspections',
    'scheduledInspections',
    'count_indication',
  ],
  panelDataSchemaVersion: LATEST_COUNTS_PANEL_DATA_SCHEMA_VERSION,
  title: 'Counts',
  isPinned: true,
}

const CountView: FC<{
  rolodexType: DrilldownRolodexType
  title: string
  filters?: ReadonlyArray<RolodexHistogramFilter>
}> = ({ rolodexType, title, filters }) => {
  const { count, isLoading } = useFilteredEntryCount(rolodexType, filters)

  return <StatDisplayCount title={title} count={count} isLoading={isLoading} />
}

export const DrilldownCountsView: FC = () => {
  const {
    panelData: { activeCountTypes },
  } = useVersionAwareDrilldownPanelData<CountsPanelData>()

  const countConfigs = useCountConfigs()

  return (
    <Container>
      {countConfigs
        .filter(
          (countConfig) =>
            countConfig && activeCountTypes.includes(countConfig.countType)
        )
        .map((countConfig) => (
          <div key={countConfig.countType}>
            {COUNT_PANEL_CUSTOM_RENDERERS[countConfig.countType] ?? (
              <CountView
                key={countConfig.countType}
                title={countConfig.title}
                rolodexType={countConfig.rolodexType}
                filters={countConfig.filters}
              />
            )}
          </div>
        ))}
    </Container>
  )
}
